import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';

/**
 * Text input dialog component (replacement for prompt())
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message
 * @param {string} placeholder - Input placeholder
 * @param {string} confirmText - Confirm button text (default: "Potwierdź")
 * @param {string} cancelText - Cancel button text (default: "Anuluj")
 * @param {function} onConfirm - Callback when user confirms (receives input value)
 * @param {function} onCancel - Callback when user cancels
 * @param {boolean} required - Whether input is required (default: true)
 * @param {string} variant - Visual variant: "default" | "danger" (default: "default")
 */
export default function TextInputDialog({
  isOpen,
  title,
  message,
  placeholder = '',
  confirmText = 'Potwierdź',
  cancelText = 'Anuluj',
  onConfirm,
  onCancel,
  required = true,
  variant = 'default'
}) {
  const [inputValue, setInputValue] = useState('');

  // Reset input when dialog opens
  useEffect(() => {
    if (isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (required && !inputValue.trim()) {
      return; // Don't confirm if required and empty
    }
    onConfirm(inputValue);
    setInputValue('');
  };

  const handleCancel = () => {
    onCancel();
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const colors = {
    default: {
      icon: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      button: 'bg-blue-600 hover:bg-blue-700'
    },
    danger: {
      icon: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
      button: 'bg-red-600 hover:bg-red-700'
    }
  };

  const style = colors[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
        {/* Close button */}
        <div className="flex justify-end mb-2">
          <button
            onClick={handleCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Header - centered icon and title */}
        <div className="flex flex-col items-center mb-6">
          <div className={`w-14 h-14 ${style.bg} ${style.border} border rounded-full flex items-center justify-center mb-4 shadow-sm`}>
            <AlertCircle className={`w-7 h-7 ${style.icon}`} />
          </div>
          <h3 className="text-xl font-bold text-slate-900 text-center">{title}</h3>
        </div>

        {/* Message */}
        {message && <p className="text-slate-600 mb-4 text-center">{message}</p>}

        {/* Input */}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus
          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all mb-6"
        />

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-medium"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={required && !inputValue.trim()}
            className={`flex-1 px-4 py-2.5 text-white rounded-xl transition-colors font-medium ${style.button} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
